import { Form, Formik } from "formik";
import * as React from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../ui/layout/page/forms-container/form-item-container/input-container";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import SectionFullWidthContainer from "../../ui/layout/page/forms-container/section-full-width-container";
import SectionItemContainer from "../../ui/layout/page/forms-container/section-item-container";
import FlowHeader from "../../ui/widgets/flow-header";
import Info from "../../ui/widgets/info";
import Link from "../../ui/widgets/link";
import InfoTooltip from "../../ui/widgets/tooltip";
import Typography from "../../ui/widgets/typography";
import CustomTypography from "../../ui/widgets/typography";
import { mapYesNoDecisionToEvent } from "../../util/tracking/eventActionMapper";
import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import { CardholderModel } from "../cardholder-basic-information/cardholder-form/types";
import RadioButtonField from "../shared/components/formik/radiobutton";
import MoreInfo from "../shared/components/more-info";
import ProductImage from "../shared/components/product-image";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import CustomerNumberField from "./customer-number";
import { CardholderBenefitsModel } from "./types";
import useValidationSchema from "./validator";
import { useContext } from "react";
import { LanguageContext } from "../../context/languageContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import { ProductContext } from "../../context/product-context/productContext";
import { YesNo } from "../general-company-information/types.tsx";

export type CardholderAdditionalBenefitsProps = {
  flowType: ApplicationFlows;
  initial: CardholderBenefitsModel;
  context: {
    companyName: string;
    cardholder: CardholderModel;
  };
  previous: () => void;
  next: (model: CardholderBenefitsModel) => Promise<void>;
};

export default function CardholderAdditionalBenefits({
  flowType,
  initial,
  context,
  previous,
  next,
}: CardholderAdditionalBenefitsProps): React.JSX.Element {
  const { getMessage } = useMessageSource();

  const { cardholder } = context;

  const { setCancelDialogActive } = useContext(CancelApplicationContext);
  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);

  return (
    <>
      <FlowHeader
        title={usePageTitle(flowType)}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={`${
          cardholder.gender === "FEMALE"
            ? getMessage("Gender.MS")
            : getMessage("Gender.MR")
        } ${cardholder.firstName} ${cardholder.lastName}`}
      />
      <ApplicationPageContent>
        <ContentHeader
          title={getMessage("CardholderBenefits.additionalServices-title")}
        />
        <Formik
          key={language}
          initialValues={initial}
          validationSchema={useValidationSchema()}
          onSubmit={async (values: CardholderBenefitsModel, formikActions) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
        >
          {({ values, handleSubmit, isSubmitting, submitCount, errors }) => {
            return (
              <Form>
                <FormContainer>
                  <SectionFullWidthContainer>
                    <SectionItemContainer>
                      <div className="scd-tooltip-wrapper scd-section-pusher-lg">
                        <CustomTypography variant="h4">
                          {getMessage(
                            "CardholderBenefits.loyaltyProgramParticipation-title",
                          )}
                        </CustomTypography>
                        <InfoTooltip
                          title={
                            <>
                              <CustomTypography
                                variant="body2"
                                dangerouslySetInnerHTML={{
                                  __html: getMessage(
                                    "CardholderBenefits.loyaltyProgramParticipation.tooltip.body",
                                  ),
                                }}
                              />
                              <CustomTypography
                                variant="subtitle2"
                                dangerouslySetInnerHTML={{
                                  __html: getMessage(
                                    "CardholderBenefits.loyaltyProgramParticipation.tooltip.subtitle",
                                  ),
                                }}
                              />
                            </>
                          }
                        >
                          <i className="icon-scd-info" />
                        </InfoTooltip>
                      </div>
                    </SectionItemContainer>
                  </SectionFullWidthContainer>
                  <RadioButtonContainer>
                    <RadioButtonField
                      wrap={false}
                      title={getMessage(
                        "IsCardholderLoyaltyParticipantQuestion.description",
                      )}
                      id="isCardholderLoyaltyParticipant"
                      name="isCardholderLoyaltyParticipant"
                      values={[
                        {
                          id: "cardholder-participant-yes",
                          value: YesNo.YES,
                          label: getMessage("YesNo.YES"),
                        },
                        {
                          id: "cardholder-participant-no",
                          value: YesNo.NO,
                          label: getMessage("YesNo.NO"),
                        },
                      ]}
                      onChangeCallback={(_, newValue) => {
                        GTM.trackEvent(
                          EventCategory.MilesAndMore,
                          mapYesNoDecisionToEvent(newValue),
                        );
                      }}
                    />
                  </RadioButtonContainer>
                  {values.isCardholderLoyaltyParticipant === "no" && (
                    <SectionItemContainer>
                      <Info>
                        {getMessage(
                          "CardholderBenefits.cardholderRequestsToParticipate",
                        )}{" "}
                        <Link
                          noicon
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getMessage(
                            "CardholderBenefits.termsAndConditions-url",
                          )}
                        >
                          {getMessage("CardholderBenefits.termsAndConditions")}
                        </Link>
                      </Info>
                    </SectionItemContainer>
                  )}
                  {values.isCardholderLoyaltyParticipant === "yes" && (
                    <InputContainer>
                      <CustomerNumberField
                        customerNumber={values.customerNumber}
                      />
                    </InputContainer>
                  )}
                  <MoreInfo
                    title={getMessage(
                      "CardholderBenefits.dataExchangeInfo-title",
                    )}
                  >
                    <Typography variant="body2">
                      {getMessage(
                        "CardholderBenefits.dataExchangeInfo-content",
                      )}
                    </Typography>
                  </MoreInfo>
                </FormContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    onCancel={() => setCancelDialogActive(true)}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    isSubmitting={isSubmitting}
                    backButtonText={getMessage("Onboarding.back")}
                    nextButtonText={getMessage("Onboarding.continue")}
                    onBack={previous}
                    onNext={handleSubmit}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
